import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const {
  FiX, FiBookOpen, FiRepeat, FiTag, FiGlobe, FiUser, FiAlignLeft,
  FiCalendar, FiClock, FiRefreshCw, FiCheck, FiKey, FiLink,
  FiToggleLeft, FiToggleRight, FiVideo, FiRadio, FiMonitor, FiWifi, FiInfo,
} = FiIcons;

const CATEGORIES        = ['Writing', 'Legal', 'Wellness', 'Planning', 'Technology', 'Finance', 'Community'];
const RECURRENCE_OPTIONS = ['None', 'Daily', 'Weekly', 'Bi-weekly', 'Monthly'];
const DURATION_OPTIONS  = ['30 min', '45 min', '60 min', '90 min', '120 min'];

const WEBINAR_PLATFORMS = [
  { id: 'none',   label: 'None',        icon: FiMonitor },
  { id: 'zoom',   label: 'Zoom',        icon: FiVideo   },
  { id: 'meet',   label: 'Google Meet', icon: FiVideo   },
  { id: 'teams',  label: 'MS Teams',    icon: FiVideo   },
  { id: 'jitsi',  label: 'Jitsi',       icon: FiWifi    },
  { id: 'native', label: 'Native',      icon: FiRadio   },
];

const STREAMING_PLATFORMS = [
  { id: 'youtube',  label: 'YouTube Live',  color: 'bg-red-500',    textColor: 'text-red-600',   borderColor: 'border-red-200',   bgLight: 'bg-red-50'    },
  { id: 'facebook', label: 'Facebook Live', color: 'bg-blue-600',   textColor: 'text-blue-600',  borderColor: 'border-blue-200',  bgLight: 'bg-blue-50'   },
  { id: 'twitch',   label: 'Twitch',        color: 'bg-purple-600', textColor: 'text-purple-600',borderColor: 'border-purple-200',bgLight: 'bg-purple-50' },
];

const WEBINAR_CREDENTIAL_FIELDS = {
  zoom:   [
    { key: 'apiKey',       label: 'API Key',         placeholder: 'Zoom API Key',                type: 'text'     },
    { key: 'apiSecret',    label: 'API Secret',       placeholder: 'Zoom API Secret',             type: 'password' },
    { key: 'meetingId',    label: 'Meeting ID',       placeholder: 'e.g. 123 456 7890',           type: 'text'     },
    { key: 'passcode',     label: 'Passcode',         placeholder: 'Meeting passcode',            type: 'password' },
  ],
  meet:   [
    { key: 'meetUrl',      label: 'Meet URL',         placeholder: 'https://meet.google.com/…',   type: 'url'      },
    { key: 'clientId',     label: 'OAuth Client ID',  placeholder: 'Google OAuth Client ID',      type: 'text'     },
    { key: 'clientSecret', label: 'OAuth Secret',     placeholder: 'Google OAuth Secret',         type: 'password' },
  ],
  teams:  [
    { key: 'tenantId',     label: 'Tenant ID',        placeholder: 'Azure Tenant ID',             type: 'text'     },
    { key: 'clientId',     label: 'Client ID',        placeholder: 'Azure App Client ID',         type: 'text'     },
    { key: 'clientSecret', label: 'Client Secret',    placeholder: 'Azure Client Secret',         type: 'password' },
    { key: 'meetingUrl',   label: 'Meeting URL',      placeholder: 'Teams meeting link',          type: 'url'      },
  ],
  jitsi:  [
    { key: 'serverUrl',    label: 'Server URL',       placeholder: 'https://meet.jit.si',         type: 'url'      },
    { key: 'roomName',     label: 'Room Name',        placeholder: 'my-webinar-room',             type: 'text'     },
    { key: 'appId',        label: 'App ID',           placeholder: 'Jitsi App ID (optional)',     type: 'text'     },
    { key: 'jwt',          label: 'JWT Token',        placeholder: 'JWT for private rooms',       type: 'password' },
  ],
  native: [
    { key: 'streamKey',    label: 'Stream Key',       placeholder: 'Internal stream key',         type: 'password' },
    { key: 'rtmpUrl',      label: 'RTMP URL',         placeholder: 'rtmp://your-server/live',     type: 'url'      },
  ],
};

const STREAMING_CREDENTIAL_FIELDS = {
  youtube:  [
    { key: 'streamKey',   label: 'Stream Key',           placeholder: 'YouTube live stream key',  type: 'password' },
    { key: 'broadcastId', label: 'Broadcast ID',         placeholder: 'YouTube broadcast ID',     type: 'text'     },
  ],
  facebook: [
    { key: 'pageToken',   label: 'Page Access Token',    placeholder: 'Facebook page token',      type: 'password' },
    { key: 'streamKey',   label: 'Stream Key',           placeholder: 'Facebook stream key',      type: 'password' },
  ],
  twitch:   [
    { key: 'streamKey',   label: 'Stream Key',           placeholder: 'Twitch stream key',        type: 'password' },
    { key: 'clientId',    label: 'Client ID',            placeholder: 'Twitch app client ID',     type: 'text'     },
  ],
};

const EMPTY_FORM = {
  title: '', seriesTitle: '', speaker: '', speakerBio: '',
  description: '', date: '', time: '', duration: '60 min',
  category: 'Wellness', recurrencePattern: 'None',
  isRecurring: false, location: 'Virtual', image: '',
};

const EMPTY_WEBINAR_CREDS = {
  apiKey: '', apiSecret: '', meetingId: '', passcode: '', meetUrl: '',
  clientId: '', clientSecret: '', tenantId: '', meetingUrl: '',
  serverUrl: '', roomName: '', appId: '', jwt: '', streamKey: '', rtmpUrl: '',
};
const EMPTY_STREAM_CREDS = { streamKey: '', broadcastId: '', pageToken: '', clientId: '' };

const STEPS = ['Details', 'Schedule', 'Platforms', 'Preview'];

const AddSeriesModal = ({ onClose, onSave }) => {
  const [form, setForm]     = useState(EMPTY_FORM);
  const [step, setStep]     = useState(1);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const [selectedWebinarPlatform, setSelectedWebinarPlatform]   = useState('none');
  const [webinarCredentials, setWebinarCredentials]             = useState({ ...EMPTY_WEBINAR_CREDS });
  const [selectedStreamingPlatforms, setSelectedStreamingPlatforms] = useState([]);
  const [streamingCredentials, setStreamingCredentials]         = useState({
    youtube: { ...EMPTY_STREAM_CREDS },
    facebook: { ...EMPTY_STREAM_CREDS },
    twitch:   { ...EMPTY_STREAM_CREDS },
  });

  const set = (key, val) => {
    setForm(prev => ({
      ...prev,
      [key]: val,
      ...(key === 'recurrencePattern' ? { isRecurring: val !== 'None' } : {}),
    }));
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: null }));
  };

  const setWebinarCred   = (key, val) => setWebinarCredentials(prev => ({ ...prev, [key]: val }));
  const setStreamingCred = (pid, key, val) =>
    setStreamingCredentials(prev => ({ ...prev, [pid]: { ...prev[pid], [key]: val } }));

  const toggleStreamingPlatform = (id) =>
    setSelectedStreamingPlatforms(prev =>
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );

  const validateStep1 = () => {
    const e = {};
    if (!form.title.trim())       e.title       = 'Session title is required';
    if (!form.speaker.trim())     e.speaker     = 'Speaker name is required';
    if (!form.description.trim()) e.description = 'Description is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep2 = () => {
    const e = {};
    if (!form.date) e.date = 'Date is required';
    if (!form.time) e.time = 'Time is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) setStep(3);
    else if (step === 3) setStep(4);
  };

  const handleSave = async () => {
    setSaving(true);
    await new Promise(r => setTimeout(r, 800));

    const platformConfig = selectedWebinarPlatform !== 'none'
      ? {
          platform: selectedWebinarPlatform,
          credentials: Object.fromEntries(
            (WEBINAR_CREDENTIAL_FIELDS[selectedWebinarPlatform] || []).map(f => [f.key, webinarCredentials[f.key] || ''])
          ),
        }
      : null;

    const streamingConfig = selectedStreamingPlatforms.map(id => ({
      platform: id,
      credentials: Object.fromEntries(
        (STREAMING_CREDENTIAL_FIELDS[id] || []).map(f => [f.key, streamingCredentials[id]?.[f.key] || ''])
      ),
    }));

    onSave?.({
      id: `web-${Date.now()}`,
      ...form,
      registrants: 0,
      isLive: false,
      image: form.image || 'https://images.unsplash.com/photo-1475721027785-f74eccf877e2?q=80&w=2070',
      platformConfig,
      streamingConfig,
    });

    setSaving(false);
    onClose();
  };

  const ic = (field) =>
    `w-full bg-brand-light/60 border rounded-2xl px-5 py-3.5 text-sm text-accent-navy placeholder-text-muted/50 focus:outline-none focus:ring-2 focus:ring-accent-gold/40 transition-all ${
      errors[field] ? 'border-red-400 focus:ring-red-200' : 'border-brand-dark focus:border-accent-gold'
    }`;

  const credIc = 'w-full bg-white border border-brand-dark rounded-xl px-4 py-3 text-sm text-accent-navy placeholder-text-muted/40 focus:outline-none focus:ring-2 focus:ring-accent-gold/40 focus:border-accent-gold transition-all';

  return (
    <div className="fixed inset-0 z-[400] flex items-center justify-center p-4 bg-accent-navy/60 backdrop-blur-md">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 24 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 24 }}
        className="bg-white rounded-[40px] shadow-2xl w-full max-w-2xl overflow-hidden border border-brand-dark flex flex-col max-h-[92vh]"
      >
        {/* ── Header ── */}
        <div className="p-8 border-b border-brand-light bg-accent-navy shrink-0">
          <div className="flex justify-between items-start mb-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-accent-gold/20 text-accent-gold rounded-2xl flex items-center justify-center border border-accent-gold/30">
                <SafeIcon icon={FiBookOpen} className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-2xl font-serif text-white">New Webinar Series</h3>
                <p className="text-[10px] font-black uppercase tracking-widest text-white/50">
                  Step {step} of {STEPS.length} — {STEPS[step - 1]}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-full text-white/60 hover:text-white transition-colors"
              aria-label="Close modal"
            >
              <SafeIcon icon={FiX} className="w-5 h-5" />
            </button>
          </div>

          {/* Step progress */}
          <div className="flex gap-2">
            {STEPS.map((s, i) => (
              <div key={s} className="flex-1 flex flex-col gap-1.5">
                <div className={`h-1 rounded-full transition-all duration-500 ${i + 1 <= step ? 'bg-accent-gold' : 'bg-white/10'}`} />
                <span className={`text-[9px] font-black uppercase tracking-widest ${i + 1 <= step ? 'text-accent-gold' : 'text-white/30'}`}>{s}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Body ── */}
        <div className="flex-grow overflow-y-auto p-8 min-h-0">
          <AnimatePresence mode="wait">

            {/* Step 1 — Details */}
            {step === 1 && (
              <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-5">
                <Field label="Session Title *" icon={FiBookOpen}>
                  <input className={ic('title')} placeholder="e.g. The Art of Legacy Writing" value={form.title} onChange={e => set('title', e.target.value)} />
                  {errors.title && <Err>{errors.title}</Err>}
                </Field>
                <Field label="Series Name" icon={FiRepeat}>
                  <input className={ic('seriesTitle')} placeholder="e.g. Legacy Builders Series (optional)" value={form.seriesTitle} onChange={e => set('seriesTitle', e.target.value)} />
                </Field>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Category" icon={FiTag}>
                    <select className={ic('category')} value={form.category} onChange={e => set('category', e.target.value)}>
                      {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                    </select>
                  </Field>
                  <Field label="Location" icon={FiGlobe}>
                    <input className={ic('location')} placeholder="Virtual / City" value={form.location} onChange={e => set('location', e.target.value)} />
                  </Field>
                </div>
                <Field label="Speaker Name *" icon={FiUser}>
                  <input className={ic('speaker')} placeholder="e.g. Dr. Julian Thorne" value={form.speaker} onChange={e => set('speaker', e.target.value)} />
                  {errors.speaker && <Err>{errors.speaker}</Err>}
                </Field>
                <Field label="Speaker Bio">
                  <textarea className={`${ic('speakerBio')} resize-none`} rows={2} placeholder="Brief bio…" value={form.speakerBio} onChange={e => set('speakerBio', e.target.value)} />
                </Field>
                <Field label="Session Description *" icon={FiAlignLeft}>
                  <textarea className={`${ic('description')} resize-none`} rows={3} placeholder="What will attendees learn?" value={form.description} onChange={e => set('description', e.target.value)} />
                  {errors.description && <Err>{errors.description}</Err>}
                </Field>
                <Field label="Cover Image URL">
                  <input className={ic('image')} placeholder="https://images.unsplash.com/…" value={form.image} onChange={e => set('image', e.target.value)} />
                </Field>
              </motion.div>
            )}

            {/* Step 2 — Schedule */}
            {step === 2 && (
              <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Date *" icon={FiCalendar}>
                    <input type="date" className={ic('date')} value={form.date} onChange={e => set('date', e.target.value)} />
                    {errors.date && <Err>{errors.date}</Err>}
                  </Field>
                  <Field label="Time *" icon={FiClock}>
                    <input type="time" className={ic('time')} value={form.time} onChange={e => set('time', e.target.value)} />
                    {errors.time && <Err>{errors.time}</Err>}
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Duration" icon={FiClock}>
                    <select className={ic('duration')} value={form.duration} onChange={e => set('duration', e.target.value)}>
                      {DURATION_OPTIONS.map(d => <option key={d}>{d}</option>)}
                    </select>
                  </Field>
                  <Field label="Recurrence" icon={FiRepeat}>
                    <select className={ic('recurrencePattern')} value={form.recurrencePattern} onChange={e => set('recurrencePattern', e.target.value)}>
                      {RECURRENCE_OPTIONS.map(r => <option key={r}>{r}</option>)}
                    </select>
                  </Field>
                </div>
                {form.isRecurring && (
                  <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="bg-purple-50 border border-purple-200 rounded-2xl p-4 flex items-start gap-3">
                    <SafeIcon icon={FiRefreshCw} className="text-purple-500 mt-0.5 shrink-0" />
                    <p className="text-xs text-purple-600 font-light">
                      Repeats <strong>{form.recurrencePattern.toLowerCase()}</strong> from the selected date.
                    </p>
                  </motion.div>
                )}
                <div className="bg-brand-light/60 border border-brand-dark rounded-2xl p-5 grid grid-cols-2 gap-x-8 gap-y-2 text-xs text-text-muted">
                  <span><strong className="text-accent-navy">Title:</strong> {form.title || '—'}</span>
                  <span><strong className="text-accent-navy">Speaker:</strong> {form.speaker || '—'}</span>
                  <span><strong className="text-accent-navy">Category:</strong> {form.category}</span>
                  <span><strong className="text-accent-navy">Location:</strong> {form.location}</span>
                </div>
              </motion.div>
            )}

            {/* Step 3 — Platforms */}
            {step === 3 && (
              <motion.div key="s3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-8">
                {/* Webinar platform */}
                <div>
                  <SectionHead icon={FiVideo} title="Webinar Integration" sub="Select the platform hosting this webinar" />
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    {WEBINAR_PLATFORMS.map(p => (
                      <button key={p.id} onClick={() => setSelectedWebinarPlatform(p.id)}
                        className={`flex items-center justify-center gap-2 px-4 py-3 rounded-2xl border text-[10px] font-black uppercase tracking-widest transition-all ${
                          selectedWebinarPlatform === p.id
                            ? 'bg-accent-navy text-white border-accent-navy shadow-lg'
                            : 'bg-brand-light/60 text-accent-navy border-brand-dark hover:border-accent-navy/40'
                        }`}>
                        <SafeIcon icon={p.icon} className="w-3.5 h-3.5" />{p.label}
                      </button>
                    ))}
                  </div>
                  <AnimatePresence mode="wait">
                    {selectedWebinarPlatform !== 'none' && WEBINAR_CREDENTIAL_FIELDS[selectedWebinarPlatform] && (
                      <motion.div key={selectedWebinarPlatform} initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                        className="bg-brand-light/40 border border-brand-dark rounded-2xl p-5 space-y-4">
                        <div className="flex items-center gap-2 mb-2">
                          <SafeIcon icon={FiKey} className="text-accent-gold w-3.5 h-3.5" />
                          <p className="text-[9px] font-black uppercase tracking-widest text-accent-navy">
                            {WEBINAR_PLATFORMS.find(p => p.id === selectedWebinarPlatform)?.label} Credentials
                          </p>
                        </div>
                        {WEBINAR_CREDENTIAL_FIELDS[selectedWebinarPlatform].map(field => (
                          <div key={field.key}>
                            <label className="block text-[9px] font-black uppercase tracking-widest text-text-muted mb-1.5">{field.label}</label>
                            <input type={field.type} className={credIc} placeholder={field.placeholder}
                              value={webinarCredentials[field.key] || ''} onChange={e => setWebinarCred(field.key, e.target.value)} autoComplete="off" />
                          </div>
                        ))}
                        <div className="flex items-center gap-2 pt-2 border-t border-brand-dark/40">
                          <SafeIcon icon={FiInfo} className="text-text-muted w-3 h-3 shrink-0" />
                          <p className="text-[9px] text-text-muted">Credentials are stored securely.</p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Streaming destinations */}
                <div>
                  <SectionHead icon={FiRadio} title="Streaming Destinations" sub="Simultaneously stream to multiple platforms" iconBg="bg-red-50" iconColor="text-red-500" />
                  <div className="space-y-3">
                    {STREAMING_PLATFORMS.map(platform => {
                      const isSelected = selectedStreamingPlatforms.includes(platform.id);
                      return (
                        <div key={platform.id} className={`border rounded-2xl overflow-hidden transition-all ${isSelected ? platform.borderColor : 'border-brand-dark'}`}>
                          <button onClick={() => toggleStreamingPlatform(platform.id)}
                            className={`w-full flex items-center justify-between px-5 py-4 transition-all ${isSelected ? platform.bgLight : 'bg-brand-light/40 hover:bg-brand-light'}`}>
                            <div className="flex items-center gap-3">
                              <div className={`w-7 h-7 ${platform.color} rounded-lg flex items-center justify-center`}>
                                <SafeIcon icon={FiRadio} className="text-white w-3.5 h-3.5" />
                              </div>
                              <span className={`text-[10px] font-black uppercase tracking-widest ${isSelected ? platform.textColor : 'text-accent-navy'}`}>{platform.label}</span>
                            </div>
                            <SafeIcon icon={isSelected ? FiToggleRight : FiToggleLeft} className={`w-6 h-6 ${isSelected ? platform.textColor : 'text-text-muted'}`} />
                          </button>
                          <AnimatePresence>
                            {isSelected && (
                              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                                <div className="px-5 pb-5 pt-3 space-y-3 bg-white border-t border-brand-dark/20">
                                  {(STREAMING_CREDENTIAL_FIELDS[platform.id] || []).map(field => (
                                    <div key={field.key}>
                                      <label className="block text-[9px] font-black uppercase tracking-widest text-text-muted mb-1.5">{field.label}</label>
                                      <input type={field.type} className={credIc} placeholder={field.placeholder}
                                        value={streamingCredentials[platform.id]?.[field.key] || ''}
                                        onChange={e => setStreamingCred(platform.id, field.key, e.target.value)} autoComplete="off" />
                                    </div>
                                  ))}
                                  <div className="flex items-center gap-2 pt-2 border-t border-brand-dark/20">
                                    <SafeIcon icon={FiLink} className="text-text-muted w-3 h-3 shrink-0" />
                                    <p className="text-[9px] text-text-muted">Stream goes live automatically when session starts.</p>
                                  </div>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}
                  </div>
                  {selectedStreamingPlatforms.length === 0 && (
                    <p className="text-[9px] text-text-muted italic mt-3 text-center">No streaming destinations — webinar will be hosted on the chosen platform only.</p>
                  )}
                </div>
              </motion.div>
            )}

            {/* Step 4 — Preview */}
            {step === 4 && (
              <motion.div key="s4" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
                <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">Review before publishing</p>
                <div className="rounded-3xl border border-brand-dark overflow-hidden shadow-sm">
                  <div className="h-40 bg-cover bg-center relative"
                    style={{ backgroundImage: `url(${form.image || 'https://images.unsplash.com/photo-1475721027785-f74eccf877e2?q=80&w=2070'})` }}>
                    <div className="absolute inset-0 bg-accent-navy/60" />
                    <div className="absolute bottom-4 left-5 right-5">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="bg-accent-gold text-accent-navy px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-widest">{form.category}</span>
                        {form.isRecurring && (
                          <span className="bg-purple-500/80 text-white px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-widest flex items-center gap-1">
                            <SafeIcon icon={FiRefreshCw} /> {form.recurrencePattern}
                          </span>
                        )}
                      </div>
                      <h4 className="text-white font-serif text-xl leading-tight">{form.title || 'Untitled Session'}</h4>
                    </div>
                  </div>
                  <div className="p-6 space-y-4 bg-white">
                    <div className="flex flex-wrap gap-6 text-[10px] font-black uppercase tracking-widest text-text-muted">
                      <span className="flex items-center gap-1.5 text-accent-gold"><SafeIcon icon={FiCalendar} /> {form.date || '—'} • {form.time || '—'}</span>
                      <span className="flex items-center gap-1.5"><SafeIcon icon={FiClock} /> {form.duration}</span>
                      <span className="flex items-center gap-1.5"><SafeIcon icon={FiGlobe} /> {form.location}</span>
                    </div>
                    {form.seriesTitle && <p className="text-[10px] font-black uppercase tracking-widest text-purple-500">{form.seriesTitle}</p>}
                    <p className="text-sm text-text-muted font-light leading-relaxed">{form.description || 'No description provided.'}</p>
                    <div className="pt-4 border-t border-brand-light flex items-start gap-3">
                      <div className="w-9 h-9 bg-accent-navy text-white rounded-xl flex items-center justify-center text-xs font-black shrink-0">
                        {(form.speaker || 'S')[0]}
                      </div>
                      <div>
                        <p className="text-sm font-black text-accent-navy">{form.speaker || 'Speaker Name'}</p>
                        <p className="text-xs text-text-muted font-light">{form.speakerBio || 'No bio provided.'}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-brand-light/50 border border-brand-dark rounded-2xl p-5 space-y-3">
                  <p className="text-[10px] font-black uppercase tracking-widest text-accent-navy mb-3">Integration Summary</p>
                  <div className="flex items-center gap-3">
                    <SafeIcon icon={FiVideo} className="text-accent-navy w-4 h-4 shrink-0" />
                    <div>
                      <p className="text-[9px] font-black uppercase tracking-widest text-text-muted">Webinar Platform</p>
                      <p className="text-sm font-bold text-accent-navy capitalize">
                        {selectedWebinarPlatform === 'none' ? 'No integration' : WEBINAR_PLATFORMS.find(p => p.id === selectedWebinarPlatform)?.label}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiRadio} className="text-red-500 w-4 h-4 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[9px] font-black uppercase tracking-widest text-text-muted">Streaming Destinations</p>
                      {selectedStreamingPlatforms.length === 0
                        ? <p className="text-sm font-bold text-text-muted">None selected</p>
                        : <div className="flex flex-wrap gap-2 mt-1">
                            {selectedStreamingPlatforms.map(id => {
                              const sp = STREAMING_PLATFORMS.find(p => p.id === id);
                              return <span key={id} className={`px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-widest text-white ${sp.color}`}>{sp.label}</span>;
                            })}
                          </div>
                      }
                    </div>
                  </div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-2xl p-4 flex items-center gap-3">
                  <SafeIcon icon={FiCheck} className="text-green-500 shrink-0" />
                  <p className="text-xs text-green-700 font-medium">This webinar will be added to the public schedule and visible to all registered users immediately after publishing.</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* ── Footer ── */}
        <div className="p-6 border-t border-brand-light bg-brand-light/30 shrink-0 flex justify-between items-center gap-4">
          <button
            onClick={step === 1 ? onClose : () => setStep(s => s - 1)}
            className="px-6 py-3 border border-brand-dark text-accent-navy rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-brand-light transition-all"
          >
            {step === 1 ? 'Cancel' : '← Back'}
          </button>
          {step < 4 ? (
            <button onClick={handleNext} className="btn-accent px-8 py-3 text-[10px] uppercase tracking-widest flex items-center gap-2">
              Continue →
            </button>
          ) : (
            <button onClick={handleSave} disabled={saving} className="btn-accent px-10 py-3 text-[10px] uppercase tracking-widest flex items-center gap-2 disabled:opacity-60">
              {saving
                ? <><SafeIcon icon={FiRefreshCw} className="animate-spin" /> Publishing…</>
                : <><SafeIcon icon={FiCheck} /> Publish Series</>
              }
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
};

/* ── Helpers ── */
const Field = ({ label, icon, children }) => (
  <div>
    {label && (
      <label className="block text-[10px] font-black uppercase tracking-widest text-accent-navy mb-2">
        {icon && <SafeIcon icon={icon} className="inline mr-1.5" />}{label}
      </label>
    )}
    {children}
  </div>
);

const Err = ({ children }) => <p className="text-red-500 text-[10px] mt-1 font-bold">{children}</p>;

const SectionHead = ({ icon, title, sub, iconBg = 'bg-accent-navy/10', iconColor = 'text-accent-navy' }) => (
  <div className="flex items-center gap-2 mb-4">
    <div className={`w-8 h-8 ${iconBg} rounded-xl flex items-center justify-center`}>
      <SafeIcon icon={icon} className={`${iconColor} w-4 h-4`} />
    </div>
    <div>
      <p className="text-[10px] font-black uppercase tracking-widest text-accent-navy">{title}</p>
      <p className="text-[9px] text-text-muted">{sub}</p>
    </div>
  </div>
);

export default AddSeriesModal;